import { type ReactNode, useState } from 'react'
import { Head } from '@inertiajs/react'
import { Check, Copy, Download } from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import CampaignWorkspace from '@/components/CampaignWorkspace'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { track } from '@/lib/track'

type ShareEvent = {
  id: string
  slug: string
  title: string
  date: string | null
  status: 'draft' | 'published'
  page_kind: 'event' | 'fundraiser'
}

type Props = {
  event: ShareEvent
  event_url: string
  landing_url: string | null
}

export default function DashboardEventShare({ event, event_url, landing_url }: Props) {
  return (
    <>
      <Head title={`Share — ${event.title}`} />
      <CampaignWorkspace event={{ ...event, landing_url }} title="Share" active="share">
        <div className="max-w-2xl space-y-8">
          <ShareSection
            id="event-url"
            label="Event page URL"
            description="The public page anyone can visit to learn about the event and buy tickets."
            url={event_url}
            eventId={event.id}
            eventSlug={event.slug}
            target="event"
          />

          {landing_url ? (
            <ShareSection
              id="landing-url"
              label="Landing page URL"
              description="A clean, brand-free version of the event page for your own website or marketing."
              url={landing_url}
              eventId={event.id}
              eventSlug={event.slug}
              target="landing"
            />
          ) : (
            <div className="rounded-xl border border-black/5 bg-muted/30 p-4 text-sm text-muted-foreground">
              <p className="font-medium text-foreground">Landing page URL</p>
              <p className="mt-1">
                A brand-free landing page is available on the Pro plan once the event is published.
              </p>
            </div>
          )}
        </div>
      </CampaignWorkspace>
    </>
  )
}

function ShareSection({
  id,
  label,
  description,
  url,
  eventId,
  eventSlug,
  target,
}: {
  id: string
  label: string
  description: string
  url: string
  eventId: string
  eventSlug: string
  target: 'event' | 'landing'
}) {
  const [copied, setCopied] = useState(false)
  const qrSrc = `/dashboard/events/${eventId}/qr?target=${target}`

  function handleCopy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true)
      track(eventId, 'share_click', { platform: 'copy', target })
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleDownload() {
    track(eventId, 'qr_download', { target })
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          value={url}
          readOnly
          className="flex-1"
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button variant="outline" size="sm" onClick={handleCopy}>
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          <span className="ml-1.5">{copied ? 'Copied' : 'Copy'}</span>
        </Button>
      </div>
      <div className="flex items-start gap-4 rounded-xl border border-black/5 bg-muted/30 p-4">
        <img
          src={qrSrc}
          alt={`QR code for ${label}`}
          className="size-40 shrink-0 rounded-md bg-white p-2 ring-1 ring-black/5"
        />
        <div className="flex-1 space-y-2">
          <p className="text-sm font-medium">QR code</p>
          <p className="text-sm text-muted-foreground">
            Scan or download to share on social media, print materials, or anywhere else.
          </p>
          <Button variant="outline" size="sm" asChild onClick={handleDownload}>
            <a href={`${qrSrc}&download=1`} download={`qr-${eventSlug}-${target}.png`}>
              <Download className="size-4" />
              <span className="ml-1.5">Download PNG</span>
            </a>
          </Button>
        </div>
      </div>
    </div>
  )
}

DashboardEventShare.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
